import { useCollectionServices } from '../../../contexts/CollectionSourceContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import OpenInFull from '@mui/icons-material/OpenInFull';
import Close from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useSecureAuth } from '../../../contexts/SecureAuthContext';
import { FitbitExtractionJob } from '../../../services/fitbitExtractionService';
import { exportSectionSx } from '../collection/ExportFormSection';
import { storedDateLabel } from './StoredDateRange';
import FitbitExtractionProgress, { extractionFileSize, extractionStatus } from './FitbitExtractionProgress';
import FitbitExtractionDetails from './FitbitExtractionDetails';
import FitbitExtractionDownload from './FitbitExtractionDownload';
import FitbitExtractionDeleteDialog from './FitbitExtractionDeleteDialog';
import FitbitExtractionCloudLink from './FitbitExtractionCloudLink';

type Props = { studyId: string; revision: number };
export default function FitbitExtractionHistory(props: Props) {
  const { user } = useSecureAuth();
  if (!user?.uid) return null;
  return <ExtractionHistory key={user.uid + ':' + props.studyId} {...props} />;
}
function ExtractionHistory({ studyId, revision }: Props) {
  const { listFitbitExtractions } = useCollectionServices();
  const [jobs, setJobs] = useState<FitbitExtractionJob[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [olderLoaded, setOlderLoaded] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedJob, setSelectedJob] = useState<FitbitExtractionJob | null>(null);
  const [deletingJob, setDeletingJob] = useState<FitbitExtractionJob | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [query, setQuery] = useState('');
  const alive = useRef(true);
  const readVersion = useRef(0);
  const filters = useRef({ search: '', state: '' });
  const activeJobs = !olderLoaded && jobs?.some(job => job.state === 'queued' || job.state === 'running');
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const refresh = useCallback(async (after?: string) => {
    const version = ++readVersion.current;
    setLoading(true); setHistoryError('');
    try {
      const page = await listFitbitExtractions(studyId, after, filters.current);
      if (!alive.current || version !== readVersion.current) return;
      setJobs(previous => after ? [...(previous || []), ...page.jobs.filter(job => !previous?.some(old => old.jobId === job.jobId))] : page.jobs);
      setCursor(page.nextCursor); setOlderLoaded(Boolean(after));
    } catch {
      if (alive.current && version === readVersion.current) setHistoryError('Could not load your extraction history. Please refresh.');
    } finally { if (alive.current && version === readVersion.current) setLoading(false); }
  }, [studyId, listFitbitExtractions]);
  useEffect(() => {
    filters.current = expanded ? { search: query, state } : { search: '', state: '' };
    setJobs(null); setCursor(null); setOlderLoaded(false);
    void refresh();
  }, [refresh, revision, query, state, expanded]);
  useEffect(() => {
    if (!activeJobs) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => { timer = setTimeout(async () => { await refresh(); if (!stopped) schedule(); }, 5000); };
    schedule();
    return () => { stopped = true; clearTimeout(timer); };
  }, [activeJobs, refresh]);
  const removed = (jobId: string) => {
    readVersion.current++;
    setLoading(false); setJobs(previous => previous?.filter(job => job.jobId !== jobId) || []);
    setDeletingJob(null);
    if (selectedJob?.jobId === jobId) setSelectedJob(null);
    void refresh();
  };
  const cards = (items: FitbitExtractionJob[]) => <Stack spacing={1.5} role="region" aria-label="Extraction job list">
    {items.map(job => <Box component="article" aria-label={job.name} key={job.jobId}
      sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: '#e7e8ec', borderRadius: '12px', bgcolor: 'white' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          <Typography variant="body2" fontWeight={650}>{job.kind === 'batch' ? job.name : job.fileName || `${job.name}.${job.format}`}</Typography>
          <Typography variant="caption" color="text.secondary">{job.kind === 'batch'
            ? `${job.participantCount} participants · ${job.completedCount || 0} of ${job.participantCount} files ready`
            : job.participantName ? `${job.participantName} · ${job.trackingId || job.participantId}` : job.trackingId || job.participantId}
            {job.kind !== 'batch' || job.sharedDates ? ` · ${storedDateLabel(job.startDate)} – ${storedDateLabel(job.endDate)}` : ' · Individual date ranges'}</Typography>
          {job.kind === 'batch' && <Typography variant="caption" display="block" color="text.secondary" sx={{ maxWidth: 520,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{job.participantNames?.join(' · ')}</Typography>}
          <Typography variant="caption" display="block" color="text.secondary">Created {new Date(job.createdAt).toLocaleString()}{job.byteCount > 0 ? ` · ${extractionFileSize(job.byteCount)}` : ''}</Typography>
          <FitbitExtractionCloudLink job={job} />
        </Box>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ flexShrink: 0 }}>
          <Chip size="small" label={extractionStatus(job)} color={job.state === 'completed' ? 'success' : job.state === 'failed' ? 'error' : 'default'} />
          <Button size="small" startIcon={<InfoOutlined />} onClick={() => setSelectedJob(job)}>View details</Button>
          {job.kind !== 'batch' && <FitbitExtractionDownload studyId={studyId} job={job} />}
          <Tooltip title={job.state === 'running' ? 'Delete after the extraction finishes' : 'Delete job'}>
            <span><IconButton size="small" aria-label={`Delete ${job.name}`} disabled={job.state === 'running'} onClick={() => setDeletingJob(job)}><DeleteOutline fontSize="small" /></IconButton></span>
          </Tooltip>
        </Stack>
      </Stack>
      {job.state !== 'completed' && <FitbitExtractionProgress job={job} />}
      {job.error && <Alert severity="error" sx={{ mt: 1.5 }}>{job.error}</Alert>}
      {job.state === 'deleting' && <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>Deletion is unfinished. Use Delete to retry.</Typography>}
    </Box>)}
  </Stack>;
  const feedback = <>
    {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}
    {jobs === null && loading && <CircularProgress size={24} aria-label="Loading extraction jobs" />}
    {jobs?.length === 0 && <Typography color="text.secondary">{expanded && (query || state) ? 'No matching jobs on this page.' : 'No extraction jobs yet.'}</Typography>}
  </>;
  return <Paper variant="outlined" sx={exportSectionSx}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} sx={{ mb: 1.5 }}>
      <Box><Typography variant="subtitle1" fontWeight={650} component="h2">Your extraction jobs</Typography>
        <Typography variant="caption" color="text.secondary">Your extractions for this study.</Typography></Box>
      <Stack direction="row" gap={1}>
        <Button size="small" startIcon={<Refresh />} disabled={loading} onClick={() => { void refresh(); }}>Refresh jobs</Button>
        <Button size="small" variant="outlined" startIcon={<OpenInFull />} onClick={() => setExpanded(true)} sx={{ borderRadius: '9px' }}>View all jobs</Button>
      </Stack>
    </Stack>
    {!expanded && <>{feedback}{cards((jobs || []).slice(0, 3))}
      {!!jobs && (jobs.length > 3 || cursor) && <Button onClick={() => setExpanded(true)} sx={{ mt: 1.5 }}>Browse older jobs</Button>}</>}
    <Dialog open={expanded} onClose={() => setExpanded(false)} fullWidth maxWidth="lg" aria-labelledby="extraction-history-title"
      PaperProps={{ sx: { borderRadius: '16px', height: 'min(90dvh, 1000px)', maxHeight: 'calc(100dvh - 24px)', m: { xs: 1, sm: 3 }, width: 'calc(100% - 24px)' } }}>
      <DialogTitle id="extraction-history-title" sx={{ pr: 7 }}>Your extraction jobs</DialogTitle>
      <IconButton aria-label="Close extraction history" onClick={() => setExpanded(false)} sx={{ position: 'absolute', right: 14, top: 14 }}><Close /></IconButton>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        <TextField size="small" fullWidth label="Search jobs" placeholder="Extraction name, tracking ID, dates or file type" value={search} onChange={event => setSearch(event.target.value)} inputProps={{ maxLength: 100 }} />
        <TextField select size="small" label="Status" value={state} onChange={event => setState(event.target.value)} sx={{ minWidth: 160 }}>
          {Object.entries({ '': 'All statuses', queued: 'Queued', running: 'Extracting', completed: 'Ready', failed: 'Failed', deleting: 'Deletion pending' }).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
        <Button startIcon={<Refresh />} disabled={loading} onClick={() => { void refresh(); }}>Refresh</Button>
      </Stack>
      <DialogContent dividers sx={{ bgcolor: '#fafafb', p: { xs: 2, sm: 3 } }}>
        {feedback}{cards(jobs || [])}
        {cursor && <Box sx={{ textAlign: 'center', mt: 2 }}><Button variant="outlined" disabled={loading} onClick={() => { void refresh(cursor); }}>{loading ? 'Loading…' : 'Load more jobs'}</Button></Box>}
      </DialogContent>
    </Dialog>
    {selectedJob && <FitbitExtractionDetails key={selectedJob.jobId} studyId={studyId} initialJob={selectedJob} onClose={() => setSelectedJob(null)} />}
    {deletingJob && <FitbitExtractionDeleteDialog key={deletingJob.jobId} studyId={studyId} job={deletingJob} onClose={() => setDeletingJob(null)} onDeleted={removed} />}
  </Paper>;
}
